import {Component, inject, OnInit} from "@angular/core";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {FormBuilder, Validators} from "@angular/forms";
import {Store} from "@ngrx/store";
import {take} from "rxjs";
import {map} from "rxjs/operators";
import {PeriodTypeEnum} from "../../../../models/asset/period-type.enum";
import {EarningBaseCode} from "../../../../../shared/model/earning-base-code";
import {AssetValuationHistoryBaseRequest} from "../../../../generated-sources/api";
import {EMPTY_CODE, EMPTY_MONEY, EMPTY_PERIOD} from "../../../../../shared/model/traceable";
import {DecimalFormatPipe} from "../../../../../shared/pipes/decimal-format/decimal-format.pipe";
import {selectGeneratedAvailablePeriodDates, selectSelectedAsset} from "../../../../store/asset/asset.selectors";
import {DateUtil} from "../../../../../shared/utils/date-util";

type CustomDialogData = {
    earningBase: string | null;
};

@Component({
    selector: "valumize-asset-history-base-custom-dialog",
    templateUrl: "./asset-history-base-custom-dialog.component.html",
    styleUrls: ["./asset-history-base-custom-dialog.component.scss"],
    standalone: false
})
export class AssetHistoryBaseCustomDialogComponent implements OnInit {
    private readonly formBuilder = inject(FormBuilder);
    private readonly store = inject(Store);
    public readonly dialogRef = inject(MatDialogRef<AssetHistoryBaseCustomDialogComponent>);
    private readonly data = inject<CustomDialogData>(MAT_DIALOG_DATA);

    private assetCurrencyIso = "";

    availableFullFiscalYearDates: string[] = [];

    get earningBase(): string | null {
        return this.data?.earningBase ?? null;
    }

    customRecordForm = this.formBuilder.group({
        displayName: this.formBuilder.control<string | null>(null),
        periodKeyDate: this.formBuilder.control<string | null>(null, {validators: [Validators.required]}),
        revenue: this.formBuilder.control<number | null>(null),
        earningsAmount: this.formBuilder.control<number | null>(null),
        netDebt: this.formBuilder.control<number | null>(null),
        enterpriseValue: this.formBuilder.control<number | null>(null),
    });

    ngOnInit(): void {
        this.store.select(selectSelectedAsset).pipe(
            take(1),
            map(a => a.data.currencyIso.code ?? "")
        ).subscribe(iso => this.assetCurrencyIso = iso);

        this.store.select(selectGeneratedAvailablePeriodDates).pipe(
            take(1)
        ).subscribe(dates => {
            const today = DateUtil.isoDate();
            this.availableFullFiscalYearDates = dates.FULL_FISCAL_YEAR.filter(d => d <= today);
        });
    }

    onCancel(): void {
        this.dialogRef.close(null);
    }

    onSave(): void {
        if (!this.customRecordForm.valid || !this.earningBase) {
            return;
        }
        const v = this.customRecordForm.value;
        const keyDate = v.periodKeyDate ?? "";
        const earningsAmount = DecimalFormatPipe.transformToMillionsNum(v.earningsAmount);
        const eb = this.earningBase;

        const request: AssetValuationHistoryBaseRequest = {
            ...(v.displayName?.trim() ? {displayName: v.displayName.trim()} : {}),
            period: {...EMPTY_PERIOD, type: PeriodTypeEnum.FullFiscalYear as any, keyDate},
            currencyIso: {...EMPTY_CODE, code: this.assetCurrencyIso || undefined},
            revenue: {...EMPTY_MONEY, amount: DecimalFormatPipe.transformToMillionsNum(v.revenue)},
            netDebt: {...EMPTY_MONEY, amount: DecimalFormatPipe.transformToMillionsNum(v.netDebt)},
            enterpriseValue: {...EMPTY_MONEY, amount: DecimalFormatPipe.transformToMillionsNum(v.enterpriseValue)},
            ebt: {...EMPTY_MONEY, amount: eb === EarningBaseCode.EBT ? earningsAmount : undefined},
            ebit: {...EMPTY_MONEY, amount: eb === EarningBaseCode.EBIT ? earningsAmount : undefined},
            ebita: {...EMPTY_MONEY, amount: eb === EarningBaseCode.EBITA ? earningsAmount : undefined},
            ebitda: {...EMPTY_MONEY, amount: eb === EarningBaseCode.EBITDA ? earningsAmount : undefined},
            ebitdar: {...EMPTY_MONEY, amount: eb === EarningBaseCode.EBITDAR ? earningsAmount : undefined},
        };
        this.dialogRef.close(request);
    }
}
